import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const RevealRequestModal = ({ match, onClose, onSubmit }) => {
  const [consent, setConsent] = useState(false);
  const [feeConsent, setFeeConsent] = useState(false);
  const [antiCircumventionConsent, setAntiCircumventionConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!consent || !feeConsent || !antiCircumventionConsent) return;
    
    setIsSubmitting(true);
    await onSubmit(match?.id);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6 border-b border-[#E5E7EB] sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Icon name="Shield" size={20} color="#F59E0B" />
              </div>
              <h3 className="text-base md:text-lg font-semibold text-[#111827]">Request Identity Reveal</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
              aria-label="Close reveal request modal"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="bg-[#F6F7FB] rounded-xl p-4">
            <div className="flex items-start gap-3 mb-3">
              <Icon name="Info" size={20} color="#5B2DFF" className="flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-sm md:text-base font-semibold text-[#111827] mb-2">Reveal Process via Groupe JM</h4>
                <p className="text-xs md:text-sm text-[#6B7280] mb-2">
                  All reveal requests are now processed exclusively through Groupe JM to ensure platform integrity and prevent circumvention. The reveal will only proceed if:
                </p>
              </div>
            </div>
            <ul className="space-y-2 ml-8">
              <li className="flex items-start gap-2 text-xs md:text-sm text-[#6B7280]">
                <Icon name="Check" size={16} className="flex-shrink-0 text-emerald-500 mt-1" />
                <span>The employer approves your reveal request</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-[#6B7280]">
                <Icon name="Check" size={16} className="flex-shrink-0 text-emerald-500 mt-1" />
                <span>Groupe JM admin reviews and approves the reveal</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-[#6B7280]">
                <Icon name="Check" size={16} className="flex-shrink-0 text-emerald-500 mt-1" />
                <span>Both parties provide explicit consent and agree to terms</span>
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="#F59E0B" className="flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-sm md:text-base font-semibold text-amber-900 mb-2">Important Notice</h4>
                <p className="text-xs md:text-sm text-amber-800">
                  Once your identity is revealed, your full professional profile including name, current employer, and contact information will be shared with this employer. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="#DC2626" className="flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-sm md:text-base font-semibold text-red-900 mb-2">Hiring Fee & Anti-Circumvention Policy</h4>
                <p className="text-xs md:text-sm text-red-800 mb-3">
                  By proceeding with this reveal request, you acknowledge that:
                </p>
                <ul className="space-y-2 text-xs md:text-sm text-red-800">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold mt-0.5">•</span>
                    <span><strong>20% Placement Fee:</strong> Any hire resulting from this reveal (whether immediate or within 24 months) will trigger a placement fee of 20% of your first-year base salary, payable by the employer.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold mt-0.5">•</span>
                    <span><strong>No Circumvention:</strong> Attempting to bypass the platform by accepting employment outside of BlinD after this reveal is strictly prohibited and may result in legal action.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold mt-0.5">•</span>
                    <span><strong>Groupe JM Control:</strong> All hiring arrangements following a reveal must be processed through Groupe JM to ensure compliance and fee collection.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm md:text-base font-semibold text-[#111827]">Match Details</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-[#6B7280]">Opportunity:</span>
                <span className="text-[#111827] font-medium">{match?.title}</span>
              </div>
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-[#6B7280]">Industry:</span>
                <span className="text-[#111827] font-medium">{match?.industry}</span>
              </div>
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-[#6B7280]">Messages Exchanged:</span>
                <span className="text-[#111827] font-medium">{match?.messageCount}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 border-t border-[#E5E7EB] pt-4">
            <h4 className="text-sm md:text-base font-semibold text-[#111827]">Required Acknowledgments</h4>
            
            <Checkbox
              label="I understand and consent to revealing my identity to this employer"
              description="This action requires Groupe JM admin approval and cannot be reversed"
              checked={consent}
              onChange={(e) => setConsent(e?.target?.checked)}
              required
            />

            <Checkbox
              label="I acknowledge the 20% placement fee structure"
              description="I understand that any employment resulting from this reveal will trigger a 20% placement fee on my first-year base salary, payable by the employer"
              checked={feeConsent}
              onChange={(e) => setFeeConsent(e?.target?.checked)}
              required
            />

            <Checkbox
              label="I agree not to circumvent the platform"
              description="I will not accept employment from this employer outside of BlinD within 24 months of this reveal, and understand that all hiring arrangements must be processed through Groupe JM"
              checked={antiCircumventionConsent}
              onChange={(e) => setAntiCircumventionConsent(e?.target?.checked)}
              required
            />
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-[#E5E7EB] flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            size="default"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="default"
            iconName="Shield"
            iconPosition="left"
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={!consent || !feeConsent || !antiCircumventionConsent}
            className="flex-1"
          >
            Submit Request
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RevealRequestModal;